import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, X, Loader2, Eye, MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { PageWrapper } from '../components/PageWrapper';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

type Filter = 'PENDING' | 'all';

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function formatAge(ts: number): string {
  const ms = Date.now() - ts;
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function OrgCreationRequestsInbox() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('PENDING');
  const [resolverNoteByReq, setResolverNoteByReq] = useState<Record<string, string>>({});
  const [showNoteByReq, setShowNoteByReq] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['orgCreationRequests', filter],
    queryFn: () => api.listOrgCreationRequests(filter),
    refetchInterval: 30_000,
  });

  const approveMut = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note?: string }) =>
      api.approveOrgCreationRequest(requestId, note),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orgCreationRequests'] }); },
  });

  const denyMut = useMutation({
    mutationFn: ({ requestId, note }: { requestId: string; note?: string }) =>
      api.denyOrgCreationRequest(requestId, note),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orgCreationRequests'] }); },
  });

  if (isLoading) return <PageSpinner />;

  const requests = data?.requests ?? [];
  const pendingCount = data?.pendingCount ?? 0;

  return (
    <PageWrapper maxWidth="4xl" spacing="none">
      <div className={cn('mb-8 flex items-center justify-between gap-3', SETTLE)} style={settleDelay(0)}>
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-ink-secondary" />
          <div>
            <h1 className="text-2xl font-bold text-white">Org Creation Requests</h1>
            <p className="text-xs text-ink-meta">
              {pendingCount} pending · founders only
            </p>
          </div>
        </div>

        <button
          onClick={() => setFilter(f => f === 'PENDING' ? 'all' : 'PENDING')}
          className={cn('flex items-center gap-1.5 rounded-md bg-white/[0.05] px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
        >
          <Eye className="h-3 w-3" strokeWidth={2.5} />
          {filter === 'PENDING' ? 'Show all' : 'Pending only'}
        </button>
      </div>

      <HelperCard
        id="org-creation-intro"
        title="How approval works"
        intro="A non-founder submits 'Create org', which lands here. Approving materialises the org rows + assigns the requester ORG_OWNER. Denying records the reason; a 24h cooldown prevents same-name re-spam."
        points={[
          { text: 'The transaction is atomic — either every org row + status flip lands, or none do. No half-states.' },
          { text: 'Slug collisions surface as 409. Tell the requester to pick a different name.' },
        ]}
        className={cn('mb-6', SETTLE)}
      />

      {requests.length === 0 ? (
        <Card className="border-none text-center">
          <p className="text-sm text-ink-secondary">
            {filter === 'PENDING' ? 'No pending org-creation requests.' : 'No org-creation requests on record.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((r, i) => {
            const isPending = r.status === 'PENDING';
            const isBusy = approveMut.isPending || denyMut.isPending;
            const note = resolverNoteByReq[r.requestId] ?? '';
            const noteOpen = !!showNoteByReq[r.requestId];
            return (
              <div key={r.requestId} className={SETTLE} style={settleDelay(i + 1)}>
              <Card className="border-none">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-lg font-black text-white truncate">{r.proposedOrgName}</h3>
                      {r.proposedOrgSlug && (
                        <span className="font-mono text-[11px] text-iron-500">/{r.proposedOrgSlug}</span>
                      )}
                      <span className={`rounded px-2 py-0.5 text-[10px] font-display font-black uppercase tracking-wider ${
                        r.status === 'PENDING' ? 'bg-amber-500/15 text-amber-300' :
                        r.status === 'APPROVED' ? 'bg-emerald-500/15 text-emerald-300' :
                        r.status === 'DENIED' ? 'bg-red-500/15 text-red-300' :
                        'bg-white/[0.06] text-iron-400'
                      }`}>{r.status}</span>
                    </div>
                    <p className="mt-1 text-xs text-iron-400">
                      <span className="font-mono">{r.requesterDisplayName ?? shortId(r.requesterDiscordId)}</span>
                      <span className="mx-2 text-iron-500">·</span>
                      <span>{formatAge(r.createdAt)}</span>
                    </p>
                    {r.reason && (
                      <div className="mt-3 rounded-md bg-white/[0.03] px-3 py-2 text-sm text-iron-200 whitespace-pre-wrap">
                        {r.reason}
                      </div>
                    )}
                    {r.resolverNote && (
                      <div className="mt-2 text-xs italic text-iron-500">
                        Resolver note: {r.resolverNote}
                      </div>
                    )}
                  </div>

                  {isPending && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        disabled={isBusy}
                        onClick={() => approveMut.mutate({ requestId: r.requestId, note: note || undefined })}
                        className={cn('flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-xs font-display font-black uppercase tracking-wider text-white disabled:opacity-50', MORPH)}
                      >
                        {approveMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" strokeWidth={2.5} />}
                        Approve
                      </button>
                      <button
                        disabled={isBusy}
                        onClick={() => denyMut.mutate({ requestId: r.requestId, note: note || undefined })}
                        className={cn('flex items-center gap-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 px-3 py-1.5 text-xs font-display font-black uppercase tracking-wider text-red-200 disabled:opacity-50', MORPH)}
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                        Deny
                      </button>
                      <button
                        onClick={() => setShowNoteByReq(s => ({ ...s, [r.requestId]: !s[r.requestId] }))}
                        className={cn('flex items-center gap-1.5 rounded-md bg-white/[0.05] hover:bg-white/[0.08] px-3 py-1.5 text-[11px] font-display font-black uppercase tracking-wider text-iron-300', MORPH)}
                      >
                        <MessageSquare className="h-3 w-3" strokeWidth={2.5} />
                        Note
                      </button>
                    </div>
                  )}
                </div>

                {isPending && noteOpen && (
                  <div className="mt-3">
                    <textarea
                      value={note}
                      onChange={e => setResolverNoteByReq(s => ({ ...s, [r.requestId]: e.target.value.slice(0, 500) }))}
                      placeholder="Optional note for the requester (max 500 chars)…"
                      className="input w-full min-h-[60px]"
                    />
                    <p className="mt-1 text-right text-[10px] text-iron-500">{note.length}/500</p>
                  </div>
                )}

                {(approveMut.isError || denyMut.isError) && approveMut.variables?.requestId === r.requestId && (
                  <p className="mt-2 text-xs text-red-400">
                    {(approveMut.error as Error | null)?.message ?? (denyMut.error as Error | null)?.message ?? 'Action failed.'}
                  </p>
                )}
              </Card>
              </div>
            );
          })}
        </div>
      )}
    </PageWrapper>
  );
}
